import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { formatDisplayName } from '@shared/utils/name.utils';
import { SiteStore } from '@core/site/site.store';
import { BasketButtonComponent } from '@features/basket';
import { ButtonComponent } from '@abook-ui/components/button/button.component';

@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    RouterLinkActive,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    BasketButtonComponent,
    ButtonComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppHeaderComponent {
  protected auth = inject(AuthenticationService);
  protected site = inject(SiteStore);
  protected displayName = computed(() => formatDisplayName(this.auth.user()?.name ?? ''));

  /**
   * True when the primary nav does not fit next to the logo and actions, so the header
   * falls back to the hamburger. The nav items come from the tenant's settings, so how
   * much room they need varies per tenant — no fixed breakpoint can be right for all.
   */
  protected readonly navCollapsed = signal(false);
  /** False until the first fit check, so an overflowing nav is never painted. */
  protected readonly navMeasured = signal(false);

  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly inner = viewChild.required<ElementRef<HTMLElement>>('inner');
  private readonly nav = viewChild.required<ElementRef<HTMLElement>>('nav');
  /** Header width the full nav needed when it last overflowed. */
  private requiredWidth = 0;

  constructor() {
    // Content that can make the full header narrower: expand again so the nav gets
    // re-measured (showing it resizes the nav, which triggers the observer below). Anything
    // that makes it wider needs no entry here — an expand that overflows collapses again.
    effect(() => {
      this.site.navItems();
      this.site.settings();
      this.displayName();
      this.navCollapsed.set(false);
    });

    afterNextRender(() => {
      const observer = new ResizeObserver(() => this.checkNavFit());
      observer.observe(this.inner().nativeElement);
      observer.observe(this.nav().nativeElement);
      this.destroyRef.onDestroy(() => observer.disconnect());
      // A nav measured in a wider fallback font may have collapsed needlessly; re-measure
      // once the web fonts are in.
      document.fonts?.ready.then(() => this.navCollapsed.set(false));
    });
  }

  protected openMenu(): void {
    this.site.openLeftDrawer();
  }

  protected signIn(): void {
    this.auth.signIn(this.router.url);
  }

  private checkNavFit(): void {
    const inner = this.inner().nativeElement;
    if (!this.navCollapsed()) {
      // Nav, logo and actions do not shrink, so a nav that does not fit overflows the row.
      if (inner.scrollWidth > inner.clientWidth) {
        this.requiredWidth = inner.scrollWidth;
        this.navCollapsed.set(true);
      }
    } else if (inner.clientWidth >= this.requiredWidth) {
      this.navCollapsed.set(false);
    }
    this.navMeasured.set(true);
  }
}
